import {StrictMode} from 'react';

import * as Sentry from '@sentry/react';
import TagManager from '@sooro-io/react-gtm-module';
import {QueryCache, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import posthog from 'posthog-js';
import ReactDOM from 'react-dom/client';
import './index.css';
import {I18nextProvider} from 'react-i18next';
import {BrowserRouter} from 'react-router-dom';

import App from './App';
import {FeatureFlagProvider} from './contexts/FeatureFlagContext/FeatureFlagProvider';
import {UserProvider} from './contexts/UserContext/UserProvider';
import {WalkthroughProvider} from './features/walkthroughs';
import {isApiResponseError} from './lib/api/v1/types';
import {AppConfig} from './lib/AppConfig';
import deDomain from './lib/locales/de-domain.json';
import de from './lib/locales/de.json';
import enDomain from './lib/locales/en-domain.json';
import en from './lib/locales/en.json';
import dePdfDomain from './lib/locales/pdf/de-pdf.json';
import enPdfDomain from './lib/locales/pdf/en-pdf.json';
import reportWebVitals from './reportWebVitals';
import {forceLogout} from './services/auth-service';

// Initialize i18next
i18n.use(LanguageDetector).init({
  fallbackLng: AppConfig.DEFAULT_LOCALE, // Default language if translation is unavailable
  debug: true, // Enable debug output in the console
  interpolation: {
    escapeValue: false, // Allow HTML tags in translations
  },
  resources: {
    en: {
      translation: en,
      domain: enDomain,
      pdfDomain: enPdfDomain,
    },
    de: {
      translation: de,
      domain: deDomain,
      pdfDomain: dePdfDomain,
    },
  },
});

function validateEnv(env?: string): boolean {
  return !!env && env !== ' ';
}

// Initialize Sentry
if (process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'staging') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration(), Sentry.breadcrumbsIntegration()],
    // Tracing
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    // Only passes errors to sentry that originate from our project.
    allowUrls: [/^https:\/\/empion(?:\.[a-z]+)?\.platform\.io$/],
    // Add other common, unuseful errors here when necessary.
    ignoreErrors: [
      /**
       * Thrown when firefox prevents an add-on from refrencing a DOM element that has been removed.
       * This can also be filtered by enabling the browser extension inbound filter
       */
      //eslint-disable-next-line
      "TypeError: can't access dead object",
      /**
       * React internal error thrown when something outside react modifies the DOM
       * This is usually because of a browser extension or Chrome's built-in translate
       */
      //eslint-disable-next-line
      "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
      //eslint-disable-next-line
      "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
    ],
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isApiResponseError(error) && error.error_code === 'AUTH-UA-1') {
        forceLogout();
        return;
      }
      // Api response errors are responses that are correctly handled in BE. No need to log them in sentry for now.
      if (!isApiResponseError(error)) {
        Sentry.captureException(error);
      }
    },
  }),
});

// GTM Manager initialization
const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID || '',
  auth: process.env.REACT_APP_AUTH_CODE || '',
  preview: process.env.REACT_APP_PREVIEW_CODE || '',
};

if (
  validateEnv(process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID) &&
  validateEnv(process.env.REACT_APP_AUTH_CODE) &&
  validateEnv(process.env.REACT_APP_PREVIEW_CODE)
) {
  TagManager.initialize(tagManagerArgs);
}

posthog.init(process.env.REACT_APP_POSTHOG_KEY || '', {
  api_host: process.env.REACT_APP_POSTHOG_HOST || '',
  defaults: '2025-05-24',
  autocapture: true,
});

if (typeof window !== 'undefined') {
  window.posthog = posthog;
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <FeatureFlagProvider>
        <BrowserRouter>
          <UserProvider>
            <I18nextProvider i18n={i18n}>
              <WalkthroughProvider>
                <App />
              </WalkthroughProvider>
            </I18nextProvider>
          </UserProvider>
        </BrowserRouter>
      </FeatureFlagProvider>
    </QueryClientProvider>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
